<template>
    <div>
        <div class="d-flex justify-content-between">
            <div class="align-self-center">
                <p class="fw-bold mb-0">{{ $t('E-post') }}</p>
            </div>

            <div class="text-end">
                <div class="position-relative">
                    <input
                        type="text"
                        v-model="userEmail"
                        class="form-control form-control-sm text-end"
                        :class="{'is-invalid': emailErrorMessage || !userEmail.length, 'is-valid': emailSaved}"
                        @input="emailSaved = false"
                        @keyup.enter="save"
                    >
                    <span v-if="emailSaved" class="fa fa-check text-success position-absolute top-50 end-0 translate-middle-y me-2" aria-label="E-post oppdatert"></span>
                </div>
            </div>
        </div>
        <div class="text-danger text-end text-sm mt-1" v-if="emailErrorMessage.length">
            {{ emailErrorMessage }}
        </div>
        <div class="d-flex justify-content-between mt-2" v-if="requiresFullFnr">
            <div class="align-self-center">
                <p class="fw-bold mb-0">{{ $t('Fødselsnummer') }}</p>
            </div>

            <div class="text-end">
                <input
                    type="text"
                    v-model="fnr"
                    inputmode="numeric"
                    maxlength="11"
                    autocomplete="off"
                    class="form-control form-control-sm text-end"
                    :class="{'is-invalid': fnrErrorMessage.length}"
                    :placeholder="$t('11 siffer')"
                    @keyup.enter="save"
                >
            </div>
        </div>
        <div class="text-danger text-end text-sm mt-1" v-if="fnrErrorMessage.length">
            {{ fnrErrorMessage }}
        </div>
        <div class="text-end mt-2">
            <span class="btn btn-success btn-sm fw-bold px-3 py-2" @click="save" :class="{'disabled':isDisabled}">
                {{ saveLabel }}
                <span v-if="isSaving" class="ml-2 fa fa-spinner fa-spin"></span>
            </span>
        </div>
        <div class="alert alert-danger mt-2" v-show="errorMessage.length">{{ errorMessage }}</div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import ProfileApi from '../../api/Profile'

const props = defineProps({
    userEmail: { type: String, default: '' },
    requiresFullFnr: { type: Boolean, default: false },
})

const userEmail = ref(props.userEmail)
const requiresFullFnr = ref(props.requiresFullFnr)
const fnr = ref('')
const isSaving = ref(false)
const isDisabled = ref(false)
const saveLabel = ref($t('Oppdater'))
const errorMessage = ref('')
const emailErrorMessage = ref('')
const fnrErrorMessage = ref('')
const emailSaved = ref(false)

const save = async () => {
    if (isSaving.value) {
        return;
    }

    emailErrorMessage.value = ''
    fnrErrorMessage.value = ''
    if (fnr.value.length) {
        const nationalNumber = L10n.nationalNumber.parse(String(fnr.value))
        if (!nationalNumber.isValidNationalNumber() || !nationalNumber.isNationalNumber()) {
            fnrErrorMessage.value = $t('Ugyldig fødselsnummer.')
            return
        }
    }

    isSaving.value = true
    isDisabled.value = true
    saveLabel.value = $t('Oppdaterer...')
    errorMessage.value = ''
    let saveSucceeded = false

    try {
        const submittedFields = ['email']
        if (fnr.value.length) {
            submittedFields.push('fnr')
        }

        const response = await ProfileApi.Update({
            email: userEmail.value,
            fnr: fnr.value,
        })
        const updatedFields = response.updatedFields ?? (response.success ? submittedFields : [])
        const notUpdatedFields = response.notUpdatedFields ?? (response.success ? [] : submittedFields)

        emailSaved.value = updatedFields.includes('email')
        if (notUpdatedFields.includes('email')) {
            emailErrorMessage.value = response.message
        }
        if (notUpdatedFields.includes('fnr')) {
            fnrErrorMessage.value = response.message
        }

        if (updatedFields.includes('fnr')) {
            fnr.value = ''
            requiresFullFnr.value = false
        }

        if (!response.success) {
            return
        }

        saveLabel.value = $t('Oppdatert!')
        saveSucceeded = true
        setTimeout(() => {
            saveLabel.value = $t('Oppdater')
            isDisabled.value = false
        }, 2000)
    } catch (e) {
        errorMessage.value = $t('Det skjedde noe galt. Prøv igjen senere.')
    } finally {
        isSaving.value = false
        if (!saveSucceeded) {
            saveLabel.value = $t('Oppdater')
            isDisabled.value = false
        }
    }
}
</script>